import { BusinessRole } from '@/types/businessRole';
import { MAX_RETRY_ATTEMPTS, RETRY_DELAY_MS } from './constants';
/**
 * Service for handling communication with the Fission multi-agent chatbot function
 */
export class FissionChatbotService {
    /**
     * Creates a new instance of FissionChatbotService
     * @param fissionUrl The URL of the Fission function
     */
    constructor(fissionUrl) {
        Object.defineProperty(this, "fissionUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.fissionUrl = fissionUrl;
    }
    /**
     * Sends a request to the Fission multi-agent chatbot function
     * @param params Request parameters including user message, chat history, environment, and business role
     * @returns Promise resolving to the formatted response
     */
    async sendRequest(params) {
        const { userMessage, environment, businessRole, userId, conversationId, isFastmodeActive, isPresentationMode, referenceId } = params;
        console.log('[TEST] FissionChatbotService - Sending request with conversationId:', conversationId);
        console.log('[TEST] FissionChatbotService - User message:', userMessage.substring(0, 50) + (userMessage.length > 50 ? '...' : ''));
        // Prepare the request payload according to FissionChatBotOptions
        const requestPayload = {
            userId,
            conversationId,
            message: userMessage,
            businessRole: businessRole || BusinessRole.PLANNER, // Default to PLANNER if not provided
            chatbotEnv: environment,
            isFastmodeActive: isFastmodeActive !== undefined ? isFastmodeActive : true, // Default to FastMode if not provided
            isPresentationMode: isPresentationMode !== undefined ? isPresentationMode : false,
            referenceId: referenceId !== undefined ? referenceId : '',
        };
        console.log('[TEST] FissionChatbotService - Request payload prepared, sending to Fission function');
        // Send the request with retry logic
        return this.sendRequestWithRetry(requestPayload);
    }
    /**
     * Sends a request to the Fission function with retry logic
     * @param requestPayload The request payload to send
     * @returns Promise resolving to the formatted response
     */
    async sendRequestWithRetry(requestPayload) {
        let attempts = 0;
        while (attempts < MAX_RETRY_ATTEMPTS) {
            try {
                console.log('Sending request to Fission function:', this.fissionUrl);
                console.log('Request payload:', JSON.stringify(requestPayload, null, 2));
                const response = await fetch(this.fissionUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestPayload)
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Fission function error:', errorText);
                    throw new Error(`Fission function error: ${response.status} ${response.statusText}`);
                }
                const data = await response.json();
                console.log('Fission function response:', data);
                console.log('[TEST] FissionChatbotService - Received response from Fission function');
                console.log('[TEST] FissionChatbotService - Response status:', data.status);
                console.log('[TEST] FissionChatbotService - Response conversationId:', data.conversationId);
                console.log('[TEST] FissionChatbotService - Response message preview:', data.message.substring(0, 50) + (data.message.length > 50 ? '...' : ''));
                console.log('[TEST] FissionChatbotService - The AI response will be saved by the Fission function, not the frontend');
                // Format the response to match the expected structure
                return {
                    content: data.message,
                    metadata: {
                        conversationId: data.conversationId,
                        requestId: data.requestId,
                        status: data.status
                    }
                };
            }
            catch (error) {
                attempts++;
                console.error(`Fission function request failed (attempt ${attempts}/${MAX_RETRY_ATTEMPTS}):`, error);
                // If we've reached the maximum number of attempts, throw the error
                if (attempts >= MAX_RETRY_ATTEMPTS) {
                    throw error;
                }
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            }
        }
        // This should never be reached due to the throw in the loop,
        // but TypeScript requires a return statement
        throw new Error('Maximum retry attempts exceeded');
    }
    /**
     * Updates the Fission function URL
     * @param fissionUrl The new Fission function URL
     */
    updateFissionUrl(fissionUrl) {
        this.fissionUrl = fissionUrl;
    }
}
// Export a singleton instance with the provided Fission function URL
export const fissionChatbotService = new FissionChatbotService((process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'multi-agent-chatbot');
